import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  Search,
  FileText,
  Copy,
  Trash2,
  Zap,
  Pencil,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import AdminLayout from "@/components/layout/AdminLayout";
import { useToast } from "@/hooks/use-toast";
import {
  fetchAiPromptTemplates,
  activateAiPromptTemplate,
  duplicateAiPromptTemplate,
  deleteAiPromptTemplate,
} from "@/api/aiPromptTemplates";
import { fetchAiPersonas } from "@/api/aiPersonas";
import {
  AiPromptTemplate,
  AiPromptPurpose,
  AI_PROMPT_PURPOSES,
} from "@/types/aiPromptTemplate";
import { AiPersona } from "@/types/aiPersona";

const AiPromptTemplatesList = () => {
  const [templates, setTemplates] = useState<AiPromptTemplate[]>([]);
  const [personas, setPersonas] = useState<AiPersona[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [purposeFilter, setPurposeFilter] = useState<string>("all");
  const [personaFilter, setPersonaFilter] = useState<string>("all");
  const [confirmAction, setConfirmAction] = useState<{
    type: "activate" | "delete";
    template: AiPromptTemplate;
  } | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [templateData, personaData] = await Promise.all([
        fetchAiPromptTemplates(),
        fetchAiPersonas(),
      ]);
      setTemplates(templateData);
      setPersonas(personaData);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast({
        title: "Error",
        description: "Failed to load prompt templates.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (template: AiPromptTemplate) => {
    try {
      await activateAiPromptTemplate(template.id);
      toast({ title: "Activated", description: `"${template.name}" is now the active template.` });
      await loadData();
    } catch (error) {
      console.error("Error activating template:", error);
      toast({ title: "Error", description: "Failed to activate template.", variant: "destructive" });
    } finally {
      setConfirmAction(null);
    }
  };

  const handleDuplicate = async (template: AiPromptTemplate) => {
    try {
      const newTemplate = await duplicateAiPromptTemplate(template.id);
      toast({ title: "Duplicated", description: `Created "${newTemplate.name}" (v${newTemplate.version}).` });
      await loadData();
    } catch (error) {
      console.error("Error duplicating template:", error);
      toast({ title: "Error", description: "Failed to duplicate template.", variant: "destructive" });
    }
  };

  const handleDelete = async (template: AiPromptTemplate) => {
    try {
      await deleteAiPromptTemplate(template.id);
      toast({ title: "Deleted", description: `"${template.name}" has been deleted.` });
      await loadData();
    } catch (error) {
      console.error("Error deleting template:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete template.",
        variant: "destructive",
      });
    } finally {
      setConfirmAction(null);
    }
  };

  const getPurposeLabel = (purpose: AiPromptPurpose) => {
    return AI_PROMPT_PURPOSES.find((p) => p.value === purpose)?.label || purpose;
  };

  const filteredTemplates = templates.filter((t) => {
    const matchesSearch =
      t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPurpose = purposeFilter === "all" || t.purpose === purposeFilter;
    const matchesPersona =
      personaFilter === "all" ||
      (personaFilter === "none" && !t.personaId) ||
      t.personaId?.toString() === personaFilter;
    return matchesSearch && matchesPurpose && matchesPersona;
  });

  if (loading) {
    return (
      <AdminLayout title="AI Prompt Templates">
        <div className="flex h-64 w-full items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="AI Prompt Templates">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <p className="text-muted-foreground">
            Manage versioned prompt templates for AI features.
          </p>
          <Button asChild>
            <Link to="/admin/ai/prompt-templates/new">
              <Plus className="h-4 w-4 mr-2" />
              New Template
            </Link>
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Templates ({filteredTemplates.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search templates..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <select
                value={personaFilter}
                onChange={(e) => setPersonaFilter(e.target.value)}
                className="px-3 py-2 border border-input bg-background rounded-md text-sm"
              >
                <option value="all">All Personas</option>
                <option value="none">No Persona</option>
                {personas.map((p) => (
                  <option key={p.id} value={p.id.toString()}>
                    {p.name}
                  </option>
                ))}
              </select>
              <select
                value={purposeFilter}
                onChange={(e) => setPurposeFilter(e.target.value)}
                className="px-3 py-2 border border-input bg-background rounded-md text-sm"
              >
                <option value="all">All Purposes</option>
                {AI_PROMPT_PURPOSES.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Persona</TableHead>
                  <TableHead>Purpose</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTemplates.map((template) => (
                  <TableRow key={template.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{template.name}</div>
                        {template.description && (
                          <div className="text-sm text-muted-foreground line-clamp-1">
                            {template.description}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {template.personaName ? (
                        <Link
                          to={`/admin/ai/personas/${template.personaId}`}
                          className="hover:underline"
                        >
                          <Badge variant="outline">{template.personaName}</Badge>
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {getPurposeLabel(template.purpose)}
                      </Badge>
                    </TableCell>
                    <TableCell>v{template.version}</TableCell>
                    <TableCell>
                      <Badge variant={template.isActive ? "default" : "secondary"}>
                        {template.isActive ? "Active" : "Draft"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {new Date(template.updatedAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="outline" size="sm" asChild>
                          <Link to={`/admin/ai/prompt-templates/${template.id}/edit`}>
                            <Pencil className="h-3 w-3 mr-1" />
                            Edit
                          </Link>
                        </Button>
                        {!template.isActive && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setConfirmAction({ type: "activate", template })
                            }
                          >
                            <Zap className="h-3 w-3 mr-1" />
                            Activate
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDuplicate(template)}
                        >
                          <Copy className="h-3 w-3 mr-1" />
                          Duplicate
                        </Button>
                        {!template.isActive && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() =>
                              setConfirmAction({ type: "delete", template })
                            }
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {filteredTemplates.length === 0 && (
              <div className="text-center py-8">
                <FileText className="mx-auto h-12 w-12 text-muted-foreground" />
                <h3 className="mt-2 text-sm font-semibold">No templates found</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  {searchTerm || purposeFilter !== "all" || personaFilter !== "all"
                    ? "Try adjusting your search criteria."
                    : "Get started by creating your first prompt template."}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Confirmation Dialog */}
      <AlertDialog
        open={!!confirmAction}
        onOpenChange={() => setConfirmAction(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction?.type === "activate"
                ? "Activate Template?"
                : "Delete Template?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction?.type === "activate"
                ? `This will deactivate the current active template for "${getPurposeLabel(confirmAction.template.purpose)}"${confirmAction.template.personaName ? ` under "${confirmAction.template.personaName}"` : ""} and activate "${confirmAction.template.name}" (v${confirmAction.template.version}).`
                : `This will permanently delete "${confirmAction?.template.name}". This action cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!confirmAction) return;
                if (confirmAction.type === "activate") {
                  handleActivate(confirmAction.template);
                } else {
                  handleDelete(confirmAction.template);
                }
              }}
              className={
                confirmAction?.type === "delete"
                  ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  : ""
              }
            >
              {confirmAction?.type === "activate" ? "Activate" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
};

export default AiPromptTemplatesList;
